/**
 * Chess Game - Complete Implementation
 * Full rules, Human vs Computer, and AI using Lichess API
 */

import React, { useState, useEffect } from 'react';
import { useLocalStorage } from './hooks/useLocalStorage';
import GameLayout from './common/GameLayout';
import ScoreDisplay from './common/ScoreDisplay';
import { GAME_CONFIG } from './config/gameConfig';

const Chess = ({ onHome }) => {
  const [gameState, setGameState] = useState(GAME_CONFIG.states.MENU);
  const [score, setScore] = useLocalStorage('chess_score', 0);
  const [highScore, setHighScore] = useLocalStorage('chess_highScore', 0);
  const [gameMode, setGameMode] = useState(null); // 'human' or 'computer'
  const [difficulty, setDifficulty] = useState(null); // 'easy', 'medium', 'hard', 'ai'
  const [board, setBoard] = useState([]);
  const [selected, setSelected] = useState(null);
  const [validMoves, setValidMoves] = useState([]);
  const [whiteTurn, setWhiteTurn] = useState(true);
  const [moves, setMoves] = useState(0);
  const [history, setHistory] = useState([]);
  const [gamePhase, setGamePhase] = useState('menu'); // menu, gamemode, difficulty, playing, gameover
  const [aiThinking, setAiThinking] = useState(false);

  // Initialize board
  const initBoard = () => {
    const newBoard = Array(64).fill(null);

    // Setup white pieces (bottom)
    const whitePieces = ['R', 'N', 'B', 'Q', 'K', 'B', 'N', 'R'];
    const whitePawns = Array(8).fill('P');

    for (let i = 0; i < 8; i++) {
      newBoard[56 + i] = { piece: whitePieces[i], color: 'white' };
      newBoard[48 + i] = { piece: 'P', color: 'white' };
    }

    // Setup black pieces (top)
    const blackPieces = ['r', 'n', 'b', 'q', 'k', 'b', 'n', 'r'];
    for (let i = 0; i < 8; i++) {
      newBoard[i] = { piece: blackPieces[i], color: 'black' };
      newBoard[8 + i] = { piece: 'p', color: 'black' };
    }

    return newBoard;
  };

  // Get valid moves for a piece
  const getValidMoves = (index, boardState = board) => {
    const piece = boardState[index];
    if (!piece) return [];

    const moves = [];
    const row = Math.floor(index / 8);
    const col = index % 8;
    const pieceLower = piece.piece.toLowerCase();

    const addMove = (r, c) => {
      if (r >= 0 && r < 8 && c >= 0 && c < 8) {
        const targetIndex = r * 8 + c;
        const target = boardState[targetIndex];
        if (!target || target.color !== piece.color) {
          moves.push(targetIndex);
        }
      }
    };

    const addMovesInDirection = (directions, maxDistance = 8) => {
      directions.forEach(([dr, dc]) => {
        for (let i = 1; i <= maxDistance; i++) {
          const nr = row + dr * i;
          const nc = col + dc * i;
          if (nr < 0 || nr >= 8 || nc < 0 || nc >= 8) break;
          const targetIndex = nr * 8 + nc;
          const target = boardState[targetIndex];
          if (!target) {
            moves.push(targetIndex);
          } else {
            if (target.color !== piece.color) moves.push(targetIndex);
            break;
          }
        }
      });
    };

    if (pieceLower === 'p') {
      const direction = piece.color === 'white' ? -1 : 1;
      const startRow = piece.color === 'white' ? 6 : 1;

      // Forward move
      const oneAhead = index + direction * 8;
      if (oneAhead >= 0 && oneAhead < 64 && !boardState[oneAhead]) {
        moves.push(oneAhead);

        // Double move from start
        if (row === startRow) {
          const twoAhead = index + direction * 16;
          if (!boardState[twoAhead]) moves.push(twoAhead);
        }
      }

      // Diagonal captures
      [-1, 1].forEach((dc) => {
        const captureIndex = index + direction * 8 + dc;
        if (captureIndex >= 0 && captureIndex < 64) {
          const captureRow = Math.floor(captureIndex / 8);
          const captureCol = captureIndex % 8;
          if (Math.abs(captureCol - col) === 1 && captureRow === row + direction) {
            const target = boardState[captureIndex];
            if (target && target.color !== piece.color) {
              moves.push(captureIndex);
            }
          }
        }
      });
    } else if (pieceLower === 'n') {
      const knightMoves = [
        [-2, -1], [-2, 1], [-1, -2], [-1, 2],
        [1, -2], [1, 2], [2, -1], [2, 1],
      ];
      knightMoves.forEach(([dr, dc]) => addMove(row + dr, col + dc));
    } else if (pieceLower === 'b') {
      addMovesInDirection([[-1, -1], [-1, 1], [1, -1], [1, 1]]);
    } else if (pieceLower === 'r') {
      addMovesInDirection([[0, 1], [0, -1], [1, 0], [-1, 0]]);
    } else if (pieceLower === 'q') {
      addMovesInDirection([
        [-1, -1], [-1, 0], [-1, 1],
        [0, -1], [0, 1],
        [1, -1], [1, 0], [1, 1],
      ]);
    } else if (pieceLower === 'k') {
      [
        [-1, -1], [-1, 0], [-1, 1],
        [0, -1], [0, 1],
        [1, -1], [1, 0], [1, 1],
      ].forEach(([dr, dc]) => addMove(row + dr, col + dc));
    }

    return moves;
  };

  // Make a move
  const makeMove = (from, to, boardState = board) => {
    // Validate indices
    if (!Number.isInteger(from) || !Number.isInteger(to) ||
        from < 0 || from >= 64 || to < 0 || to >= 64) {
      console.error('Invalid move indices:', { from, to });
      return boardState;
    }

    const newBoard = boardState.map((p) => p);
    const piece = boardState[from];

    if (!piece) {
      console.error('No piece at index:', from);
      return boardState; // No piece to move
    }

    newBoard[to] = piece;
    newBoard[from] = null;

    // Pawn promotion
    if (piece.piece && piece.piece.toLowerCase() === 'p') {
      const row = Math.floor(to / 8);
      if ((piece.color === 'white' && row === 0) || (piece.color === 'black' && row === 7)) {
        newBoard[to] = {
          ...piece,
          piece: piece.color === 'white' ? 'Q' : 'q'
        };
      }
    }

    return newBoard;
  };

  // Check if king is in check
  const isKingInCheck = (boardState, color) => {
    const kingPiece = color === 'white' ? 'K' : 'k';
    const kingIndex = boardState.findIndex((p) => p && p.piece === kingPiece);

    if (kingIndex === -1) return false;

    // Check if any opponent piece can attack the king
    const opponentColor = color === 'white' ? 'black' : 'white';
    for (let i = 0; i < 64; i++) {
      const piece = boardState[i];
      if (piece && piece.color === opponentColor) {
        const moves = getValidMoves(i, boardState);
        if (moves.includes(kingIndex)) return true;
      }
    }

    return false;
  };

  // Get best move from Lichess API
  const getBestMoveFromAI = async (boardState, color) => {
    try {
      // Convert board to FEN
      const fen = boardToFen(boardState);
      console.log('Requesting AI move for FEN:', fen);

      // Use Lichess API
      const response = await fetch(
        `https://lichess.org/api/cloud-eval?fen=${encodeURIComponent(fen)}`
      );

      if (!response.ok) {
        console.error('API response not ok:', response.status);
        return null;
      }

      const data = await response.json();
      console.log('API response:', data);

      if (data.pvs && data.pvs.length > 0 && data.pvs[0].moves) {
        const moveStr = data.pvs[0].moves[0];
        console.log('Move string from API:', moveStr);
        const move = fenMoveToIndices(moveStr);
        console.log('Converted move:', move);
        return move;
      }
    } catch (err) {
      console.error('AI API error:', err);
    }
    return null;
  };

  // Simple AI moves (Easy, Medium, Hard)
  const getSimpleAIMove = (boardState, level) => {
    const possibleMoves = [];

    for (let i = 0; i < 64; i++) {
      const piece = boardState[i];
      if (piece && piece.color === 'black') {
        const moves = getValidMoves(i, boardState);
        moves.forEach((to) => {
          possibleMoves.push({ from: i, to });
        });
      }
    }

    if (possibleMoves.length === 0) return null;

    // Easy: random move
    if (level === 'easy') {
      return possibleMoves[Math.floor(Math.random() * possibleMoves.length)];
    }

    // Medium: prefer captures and checks
    if (level === 'medium') {
      const scoredMoves = possibleMoves.map((move) => {
        let score = 0;
        const target = boardState[move.to];
        if (target) score += 10; // Capture
        const newBoard = makeMove(move.from, move.to, boardState);
        if (isKingInCheck(newBoard, 'white')) score += 5; // Check
        return { ...move, score };
      });
      const maxScore = Math.max(...scoredMoves.map((m) => m.score));
      const bestMoves = scoredMoves.filter((m) => m.score >= maxScore - 3);
      return bestMoves[Math.floor(Math.random() * bestMoves.length)];
    }

    // Hard: minimax-like (prefer captures, checks, and piece value)
    if (level === 'hard') {
      const piecesValue = { p: 1, n: 3, b: 3, r: 5, q: 9, k: 100 };
      const scoredMoves = possibleMoves.map((move) => {
        let score = 0;
        const target = boardState[move.to];
        if (target) score += piecesValue[target.piece] * 10;
        const newBoard = makeMove(move.from, move.to, boardState);
        if (isKingInCheck(newBoard, 'white')) score += 50;
        return { ...move, score };
      });
      const maxScore = Math.max(...scoredMoves.map((m) => m.score));
      const bestMoves = scoredMoves.filter((m) => m.score === maxScore);
      return bestMoves[Math.floor(Math.random() * bestMoves.length)];
    }

    return null;
  };

  // Convert board to FEN (complete format for Lichess API)
  const boardToFen = (boardState) => {
    let fen = '';

    // Board position
    for (let row = 0; row < 8; row++) {
      let emptyCount = 0;
      for (let col = 0; col < 8; col++) {
        const piece = boardState[row * 8 + col];
        if (piece) {
          if (emptyCount > 0) {
            fen += emptyCount;
            emptyCount = 0;
          }
          fen += piece.piece;
        } else {
          emptyCount++;
        }
      }
      if (emptyCount > 0) fen += emptyCount;
      if (row < 7) fen += '/';
    }

    // Add active color (black's turn since we call this after white moves)
    fen += ' b KQkq - 0 1';

    return fen;
  };

  // Convert move notation to indices
  const fenMoveToIndices = (moveStr) => {
    const fromCol = moveStr.charCodeAt(0) - 'a'.charCodeAt(0);
    const fromRow = 8 - parseInt(moveStr[1]);
    const toCol = moveStr.charCodeAt(2) - 'a'.charCodeAt(0);
    const toRow = 8 - parseInt(moveStr[3]);
    return {
      from: fromRow * 8 + fromCol,
      to: toRow * 8 + toCol,
    };
  };

  // Start new game
  const startGame = () => {
    const newBoard = initBoard();
    setBoard(newBoard);
    setSelected(null);
    setValidMoves([]);
    setWhiteTurn(true);
    setMoves(0);
    setScore(0);
    setHistory([]);
    setGamePhase('playing');
    setGameState(GAME_CONFIG.states.PLAYING);
  };

  // Handle cell click
  const handleCellClick = (index) => {
    if (gamePhase !== 'playing' || gameState !== GAME_CONFIG.states.PLAYING) return;
    if (gameMode === 'computer' && !whiteTurn && difficulty !== 'ai') return; // Don't allow clicks during computer turn

    const piece = board[index];

    if (validMoves.includes(index)) {
      // Make move
      const newBoard = makeMove(selected, index, board);
      setBoard(newBoard);
      setHistory([...history, { from: selected, to: index }]);
      setWhiteTurn(!whiteTurn);
      setMoves(moves + 1);
      setScore((prev) => prev + 10);
      setSelected(null);
      setValidMoves([]);

      // Check for computer move
      if (gameMode === 'computer' && difficulty) {
        setAiThinking(true);
        setTimeout(async () => {
          const computerColor = 'black';
          let computerMove = null;

          if (difficulty === 'ai') {
            try {
              computerMove = await getBestMoveFromAI(newBoard, computerColor);
            } catch (err) {
              console.error('AI error:', err);
              computerMove = getSimpleAIMove(newBoard, 'easy'); // Fallback to easy
            }
          } else {
            computerMove = getSimpleAIMove(newBoard, difficulty);
          }

          if (computerMove && Number.isInteger(computerMove.from) && Number.isInteger(computerMove.to)) {
            console.log('Executing computer move:', computerMove);
            const computerBoard = makeMove(computerMove.from, computerMove.to, newBoard);
            setBoard(computerBoard);
            setHistory((prev) => [...prev, { from: selected, to: index }, computerMove]);
            setMoves((prev) => prev + 1);
            setWhiteTurn(true);
          } else {
            console.warn('Invalid computer move, using fallback:', computerMove);
            const fallbackMove = getSimpleAIMove(newBoard, 'easy');
            if (fallbackMove && Number.isInteger(fallbackMove.from) && Number.isInteger(fallbackMove.to)) {
              const computerBoard = makeMove(fallbackMove.from, fallbackMove.to, newBoard);
              setBoard(computerBoard);
              setHistory((prev) => [...prev, { from: selected, to: index }, fallbackMove]);
              setMoves((prev) => prev + 1);
              setWhiteTurn(true);
            }
          }
          setAiThinking(false);
        }, 600);
      }
    } else if (piece && ((piece.color === 'white' && whiteTurn) || (piece.color === 'black' && !whiteTurn && gameMode === 'human'))) {
      setSelected(index);
      setValidMoves(getValidMoves(index, board));
    } else {
      setSelected(null);
      setValidMoves([]);
    }
  };

  const resetGame = () => {
    if (moves > highScore) setHighScore(moves);
    setGamePhase('menu');
    setGameState(GAME_CONFIG.states.MENU);
    setGameMode(null);
    setDifficulty(null);
  };

  const getPieceSymbol = (piece) => {
    const symbols = { P: '♙', R: '♖', N: '♘', B: '♗', Q: '♕', K: '♔', p: '♙', r: '♖', n: '♘', b: '♗', q: '♕', k: '♔' };
    return symbols[piece.piece] || '';
  };

  return (
    <GameLayout
      title="Chess ♟️"
      score={score}
      highScore={highScore}
      gameState={gameState}
      onRestart={resetGame}
      onHome={onHome}
    >
      <div style={styles.gameArea}>
        {/* Menu - Game Mode Selection */}
        {gamePhase === 'menu' && (
          <div style={styles.menuContainer}>
            <h2 style={styles.menuTitle}>Chess</h2>
            <p style={styles.menuDescription}>Choose your opponent</p>
            <div style={styles.modeButtonsContainer}>
              <button
                onClick={() => {
                  setGameMode('human');
                  startGame();
                }}
                className="btn btn-primary btn-lg"
              >
                👥 Human vs Human
              </button>
              <button
                onClick={() => {
                  setGameMode('computer');
                  setGamePhase('difficulty');
                }}
                className="btn btn-success btn-lg"
              >
                🤖 Human vs Computer
              </button>
            </div>
          </div>
        )}

        {/* Difficulty Selection */}
        {gamePhase === 'difficulty' && (
          <div style={styles.menuContainer}>
            <h2 style={styles.menuTitle}>Select Difficulty</h2>
            <div style={styles.difficultyButtonsContainer}>
              <button
                onClick={() => {
                  setDifficulty('easy');
                  startGame();
                }}
                className="btn btn-primary"
              >
                🟢 Easy
              </button>
              <button
                onClick={() => {
                  setDifficulty('medium');
                  startGame();
                }}
                className="btn btn-warning"
              >
                🟡 Medium
              </button>
              <button
                onClick={() => {
                  setDifficulty('hard');
                  startGame();
                }}
                className="btn btn-danger"
              >
                🔴 Hard
              </button>
              <button
                onClick={() => {
                  setDifficulty('ai');
                  startGame();
                }}
                className="btn btn-info"
              >
                ⚡ AI (Lichess)
              </button>
            </div>
          </div>
        )}

        {/* Chess Board */}
        {gamePhase === 'playing' && (
          <div style={styles.playingArea}>
            <div style={styles.turnInfo}>
              {aiThinking ? 'AI is thinking...' : `${whiteTurn ? 'White' : 'Black'}'s Turn`}
            </div>
            {/* Board with coordinates */}
            <div style={styles.boardWithCoordinates}>
              {/* Rank numbers (left side) */}
              <div style={styles.rankLabelsContainer}>
                {['8', '7', '6', '5', '4', '3', '2', '1'].map((rank) => (
                  <div key={rank} style={styles.rankLabel}>{rank}</div>
                ))}
              </div>

              {/* Chess Board */}
              <div>
                <div style={styles.chessBoard}>
                  {board.map((piece, idx) => {
                    const row = Math.floor(idx / 8);
                    const col = idx % 8;
                    const isLight = (row + col) % 2 === 0;
                    const isSelected = selected === idx;
                    const isValidMove = validMoves.includes(idx);

                    let cellBgColor = isSelected
                      ? '#FFD700'
                      : isValidMove
                      ? '#90EE90'
                      : isLight
                      ? '#f0d9b5'
                      : '#b58863';

                    return (
                      <div
                        key={idx}
                        onClick={() => handleCellClick(idx)}
                        style={{
                          ...styles.cell,
                          backgroundColor: cellBgColor,
                          cursor: 'pointer',
                          boxShadow: isSelected ? 'inset 0 0 5px rgba(0, 0, 0, 0.3)' : 'none',
                          transition: 'all 0.2s ease',
                        }}
                      >
                        {piece && (
                          <span
                            style={{
                              fontSize: '2.8rem',
                              fontWeight: 'bold',
                              lineHeight: '1',
                              color: piece.color === 'white' ? '#ffffff' : '#000000',
                              textShadow: piece.color === 'white'
                                ? '1px 1px 2px #000, -1px -1px 2px #000, 1px -1px 2px #000, -1px 1px 2px #000'
                                : '1px 1px 2px #fff, -1px -1px 2px #fff, 1px -1px 2px #fff, -1px 1px 2px #fff',
                            }}
                          >
                            {getPieceSymbol(piece)}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* File labels (bottom) */}
                <div style={styles.fileCoordinates}>
                  {['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'].map((f) => (
                    <div key={f} style={styles.fileLabel}>{f}</div>
                  ))}
                </div>
              </div>
            </div>
            <div style={styles.infoContainer}>
              <div>Moves: {moves}</div>
              <div>{gameMode === 'computer' && difficulty ? `Difficulty: ${difficulty.toUpperCase()}` : 'Human vs Human'}</div>
            </div>
          </div>
        )}
      </div>
    </GameLayout>
  );
};

const styles = {
  gameArea: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '1.5rem',
    width: '100%',
  },
  menuContainer: {
    backgroundColor: `rgba(0, 0, 0, 0.5)`,
    border: `2px solid ${GAME_CONFIG.colors.primary}`,
    borderRadius: GAME_CONFIG.radius.lg,
    padding: '3rem 2rem',
    textAlign: 'center',
    minWidth: '400px',
  },
  menuTitle: {
    color: GAME_CONFIG.colors.accent,
    fontSize: '2rem',
    marginBottom: '1rem',
    fontWeight: 'bold',
  },
  menuDescription: {
    color: GAME_CONFIG.colors.light,
    marginBottom: '2rem',
  },
  modeButtonsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  difficultyButtonsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
  playingArea: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '1rem',
  },
  boardWithCoordinates: {
    display: 'flex',
    gap: '0.5rem',
    alignItems: 'flex-start',
  },
  rankLabelsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0',
    paddingTop: '2px',
  },
  rankLabel: {
    width: '25px',
    height: '55px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: GAME_CONFIG.colors.light,
    fontSize: '0.85rem',
    fontWeight: 'bold',
  },
  turnInfo: {
    color: GAME_CONFIG.colors.light,
    fontSize: '1.1rem',
    padding: '0.75rem 1.5rem',
    backgroundColor: `rgba(0, 212, 255, 0.1)`,
    border: `1px solid ${GAME_CONFIG.colors.accent}`,
    borderRadius: GAME_CONFIG.radius.md,
    fontWeight: 'bold',
    boxShadow: `0 4px 8px rgba(0, 212, 255, 0.2)`,
  },
  chessBoard: {
    display: 'grid',
    gridTemplateColumns: 'repeat(8, 55px)',
    gap: '0',
    border: `4px solid #333`,
    borderRadius: '6px',
    backgroundColor: '#b58863',
    boxShadow: `0 8px 16px rgba(0, 0, 0, 0.4), inset 0 0 10px rgba(0, 0, 0, 0.2)`,
    padding: '2px',
  },
  cell: {
    width: '55px',
    height: '55px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    position: 'relative',
    userSelect: 'none',
  },
  fileCoordinates: {
    display: 'flex',
    gap: '0',
    justifyContent: 'flex-start',
    marginLeft: '25px',
  },
  fileLabel: {
    width: '55px',
    textAlign: 'center',
    color: GAME_CONFIG.colors.light,
    fontSize: '0.85rem',
    fontWeight: 'bold',
    marginTop: '0.25rem',
  },
  infoContainer: {
    display: 'flex',
    gap: '2rem',
    color: GAME_CONFIG.colors.light,
    padding: '0.75rem 1.5rem',
    backgroundColor: `rgba(0, 212, 255, 0.1)`,
    border: `1px solid ${GAME_CONFIG.colors.accent}`,
    borderRadius: GAME_CONFIG.radius.md,
    boxShadow: `0 4px 8px rgba(0, 212, 255, 0.2)`,
    fontWeight: 'bold',
  },
};

export default Chess;
